import axios from 'axios';
import { Moment } from 'moment';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { formatDossierContactTargets } from '@/shared/converters/dossierContactTargetConverters';
import DossierContactTarget from '@/shared/models/DossierContactTarget';
import { OutletId } from '@/shared/models/Outlet';
import { present } from '@/shared/utils/typescript';
import { parseAvailabilities } from '../converters/availabilityConverters';
import { parseDossierDetails } from '../converters/dossierDetailsConverters';
import { parseIndicators } from '../converters/indicatorConverters';
import { parsePlannerCalendar } from '../converters/plannerCalendarConverters';
import { formatPlannerDossier } from '../converters/plannerDossierConverters';
import { parsePlannerEvents } from '../converters/plannerEventConverters';
import { parsePlannerResources } from '../converters/plannerResourceConverters';
import AbsenceType from '../models/AbsenceType';
import Availability from '../models/Availability';
import DossierDetails from '../models/DossierDetails';
import Indicator from '../models/Indicator';
import PlannerCalendar from '../models/PlannerCalendar';
import PlannerDossier from '../models/PlannerDossier';
import PlannerEvent from '../models/PlannerEvent';
import PlannerResource from '../models/PlannerResource';
import { Month } from '../models/common';
import { PlannerEventId, PlannerResourceId } from '../models/ids';
import PlannerEventCreated from '../models/PlannerEventCreated';
import { parsePlannerEventCreated } from '../converters/plannerEventCreatedConverters';

export interface ResourceContainer {
  resources: Array<PlannerResource>;
  availabilities: Array<Availability>;
}

export interface AbsenceData {
  start: Moment;
  end: Moment;
  type: AbsenceType;
  resource: PlannerResource;
}

export default class WorkshopPlanningClient {
  /**
   * Load all Resources = Mechanics
   */
  static async loadResources(outletId: OutletId, teamId: WorkshopTeamId, date: Moment): Promise<ResourceContainer> {
    const params = {
      moid: `${outletId}`,
      date: formatMoment(date, DateTimeFormat.PLANNER_DATE),
      team: `${teamId ?? ''}`,
    };
    const response = await axios.get('/api/mobopro/v1/planningresources', { params });

    const resources = parsePlannerResources(response.data.resources);
    const availabilities = parseAvailabilities(response.data.availability);

    return { resources, availabilities };
  }

  /**
   * Get calendar data for workshop planning
   */
  static async loadCalendar(outletId: OutletId, teamId: WorkshopTeamId, month: Month, year: number): Promise<PlannerCalendar> {
    const params = {
      moid: `${outletId}`,
      month: `${month}`,
      year: `${year}`,
      teamId: `${teamId ?? ''}`,
    };
    const response = await axios.get('/api/mobopro/v1/planningcalendar', { params });
    return present(parsePlannerCalendar(response.data));
  }

  static async loadEvents(outletId: OutletId, teamId: WorkshopTeamId | null, date: Moment, outLetUserIds: Array<PlannerResourceId>): Promise<Array<PlannerEvent>> {
    const params = {
      moid: `${outletId}`,
      date: formatMoment(date, DateTimeFormat.PLANNER_DATE),
      team: `${teamId ?? ''}`,
      ouids: outLetUserIds,
    };
    const response = await axios.get('/api/mobopro/v1/planningevents', { params });
    return parsePlannerEvents(response.data.plannedWork);
  }

  static async loadOffPlanning(outletId: OutletId, teamId: WorkshopTeamId): Promise<Array<PlannerEvent>> {
    const params = {
      moid: `${outletId}`,
      team: `${teamId ?? ''}`,
    };
    const response = await axios.get('/api/mobopro/v1/offplanningevents', { params });
    const events = parsePlannerEvents(response.data);
    // Backend returns default duration for unplanned events, which does not correspond to the dossier
    // workaround: hide these values
    events.forEach(e => e.duration = '');
    return events;
  }

  static async loadDossierDetails(dossierId: DossierId): Promise<DossierDetails> {
    const response = await axios.get(`/api/mobopro/v1/dossierdetails/${dossierId}`);
    return present(parseDossierDetails(response.data));
  }

  static async reschedule(eventId: PlannerEventId, scheduledFrom: Moment, scheduledTill: Moment, assignedTo: PlannerResourceId): Promise<void> {
    await axios.put(`/api/mobopro/v1/planning/${eventId}/reschedule`, {
      scheduledFrom: formatMoment(scheduledFrom, DateTimeFormat.LEGACY),
      scheduledTill: formatMoment(scheduledTill, DateTimeFormat.LEGACY),
      assignedTo,
    });
  }

  static async createEvent(outletId: OutletId, event: PlannerEvent): Promise<PlannerEventCreated> {
    const response = await axios.post('/api/mobopro/v2/planning', {
      moboOutletId: outletId,
      assignedTo: event.resourceId,
      dossierId: event.dossierId,
      workshopPlanningId: event.id,
      scheduledFrom: formatMoment(event.start, DateTimeFormat.LEGACY),
      scheduledTill: formatMoment(event.end, DateTimeFormat.LEGACY),
    });

    return present(parsePlannerEventCreated(response.data));
  }

  static async createInternalEvent(outletId: OutletId, event: PlannerEvent): Promise<void> {
    await axios.post('/api/mobopro/v1/planninginternal', {
      moboOutletId: outletId,
      assignedTo: event.resourceId,
      description: event.items[0],
      scheduledFrom: formatMoment(event.start, DateTimeFormat.LEGACY),
      scheduledTill: formatMoment(event.end, DateTimeFormat.LEGACY),
      isFixedTimeslot: !event.editable,
    });
  }

  static async deleteEvent(eventId: PlannerEventId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/planning/${eventId}`);
  }

  static async updateEventLock(eventId: PlannerEventId, lock: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/planning/${eventId}`, { isFixedTimeslot: lock });
  }

  static async sendConfirmation(eventId: PlannerEventId, appointment: Moment | null, preferredPickUp: Moment | null, targets: Array<DossierContactTarget>): Promise<void> {
    const data = {
      workshopPlanningId: eventId,
      appointment: formatMoment(appointment, DateTimeFormat.LEGACY),
      preferredPickUp: formatMoment(preferredPickUp, DateTimeFormat.LEGACY),
      targets: formatDossierContactTargets(targets),
    };
    await axios.post('/api/mobopro/v1/planningconfirmation', data);
  }

  static async createPlanningDossier(plannerDossier: PlannerDossier): Promise<DossierId> {
    const data = formatPlannerDossier(plannerDossier);
    const response = await axios.post('/api/mobopro/v1/planningdossier', data);
    return response.data.id;
  }

  static async closeDay(outletId: OutletId): Promise<void> {
    const params = {
      moid: `${outletId}`,
    };
    await axios.post('/api/mobopro/v1/planningclosure', undefined, { params });
  }

  static async createAbsence(outletId: OutletId, data: AbsenceData): Promise<void> {
    const dateTimeFormat = data.start.isSame(data.end, 'day') ? DateTimeFormat.PLANNER_DATE_TIME : DateTimeFormat.PLANNER_DATE;
    await axios.post('/api/mobopro/v1/planningabsences', {
      assignedTo: data.resource.id,
      moboOutletId: outletId,
      absentFrom: formatMoment(data.start, dateTimeFormat),
      absentTill: formatMoment(data.end, dateTimeFormat),
      absenceTypeId: data.type.id,
    });
  }

  static async loadIndicators(outletId: OutletId): Promise<Array<Indicator>> {
    const params = { moid: outletId };
    const response = await axios.get('/api/mobopro/v1/workshopplanningindicators', { params });
    return parseIndicators(response.data, outletId);
  }
}
